import { useEffect, useState } from "react";
import { useNotification } from "hooks/useNotification";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import FormInput from "components/blocks/FormInput";
import Switch from "components/blocks/Switch";
import Accordion from "components/blocks/Accordion";
import AccordionItem from "components/blocks/Accordion/AccordionItem";
import LookupDropdown from "components/blocks/LookupDropdown";
import Dropdown from "components/blocks/Dropdown";
import FormBuilderComponent from "components/formBuilder/FormBuilderComponent";
import { useBreadcrumb } from "contexts/breadcrumb/BreadcrumbContext";
import { CreatePublishingFormRequest , createTemplatesFormSchema } from "views/home/formTemplates/FormView/templatesForm.schema";
import { FormTemplatesService } from "services/formTemplatesService";
import { PublishingFormStatus } from "services/formTemplatesService/models/formTemplatesTypes";
import FormTextArea from "components/blocks/FormTextArea";

const FormTemplateFormAddUpdateView = () => {
  // Hooks
  const { t } = useTranslation("FormTemplates");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const { setBreadcrumbs } = useBreadcrumb();

  // State
  const [formTemplateFormSerialNumber, setformTemplateFormSerialNumber] = useState<string | null>(null);
  const formTemplateFormId = searchParams.get("id");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (formTemplateFormId) {
      fetchformTemplateForm(+formTemplateFormId);
    }
  }, [formTemplateFormId]);

  const fetchformTemplateForm = async (formTemplateFormId: number) => {
    setLoading(true);
    const response = await FormTemplatesService.getById(formTemplateFormId);
    console.log(response.data.jsonSchema);

    if (response.hasError) {
      showNotification({ description: response.description, type: "error" });
      navigate(-1);
    } else {
      console.log(response.data);
       const mappedResponse = {
        typeOfScientificId: response.data.typeOfScientific.id,
        scientificClassificationId:response.data.scientificClassification.id,
        status: response.data.status,
        publishingFormDetails:response.data.publishingFormDetails,
        jsonSchema:  response.data.jsonSchema === "{{}}" ? "" : response.data.jsonSchema || "",
      }; 

      reset(mappedResponse);
    }
    setLoading(false);
  };

  const onSubmit = async (data : CreatePublishingFormRequest) =>
  {
    try {
      const response = formTemplateFormId ?
      await FormTemplatesService.update(+formTemplateFormId,data)
      : await FormTemplatesService.create(data);

      if (response.hasError) {
        showNotification({ description: response.description, type: "error" });
      } else {
        showNotification({ description: t("Common:success"), type: "success" });
        navigate(-1);
      }
    } catch {
      showNotification({ description: t("Common:internalServerError"), type: "error" });
    } finally {
      setSubmitting(false);
    }

  }
  
  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    getValues,
    setValue,
    reset,
  } = useForm<CreatePublishingFormRequest>({
    resolver: zodResolver(createTemplatesFormSchema),
    values: {
      status: "Active",
      typeOfScientificId: 0,
      scientificClassificationId: 0,
      jsonSchema: "{}",
      publishingFormDetails: [
        {
          displayName: "",
          description: "",
          locale: "Ar",
        },
        {
          displayName: "",
          description: "",
          locale: "En",
        },
      ]
    },
  });

  return (
    <form className="position-relative" noValidate onSubmit={handleSubmit(onSubmit)} >
      <LoadingOverlay visible={loading} className="position-fixed" />

      {/* Page Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2 align-items-start">
          <div>
            <h4 className="text-dark">{formTemplateFormId ? t("updateForm") : t("addForm")}</h4>
            {formTemplateFormSerialNumber && <span>{formTemplateFormSerialNumber}</span>}
          </div>
          <Switch
            checked={watch("status") === "Active"}
            onChange={(checked) => {
              setValue("status", checked ? "Active" : "InActive");
            }}
          />
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-primary px-3" type="submit" disabled={submitting}>
            {submitting && <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true" />}
            {t("Common:save")}
          </button>
          <button
            className="btn btn-outline-secondary px-3"
            type="button"
            disabled={submitting}
            onClick={() => navigate(-1)}
          >
            {t("Common:cancel")}
          </button>
        </div>
      </div>

      <div className="divider" />

      {/* Main Info */}
      <Accordion>
        <AccordionItem initiallyOpen title={t("mainInfo")} elementId={1}>
          <div className="row mb-3">
            <FormInput
              wrapperClassName="col-12"
              label={t("formNameAr")}
              {...register("publishingFormDetails.0.displayName")}
              error={t(errors.publishingFormDetails?.[0]?.displayName?.message ?? "")}
            />
            <FormTextArea
              {...register("publishingFormDetails.0.description")}
              error={t(errors.publishingFormDetails?.[0]?.description?.message ?? "", { length: 3 })}
              wrapperClassName="col-12"
              label={t("formDescAr")}
            />
          </div>
          <div className="row mb-3">
            <FormInput
              wrapperClassName="col-12"
              label={t("formNameEn")}
              {...register("publishingFormDetails.1.displayName")}
              error={t(errors.publishingFormDetails?.[1]?.displayName?.message ?? "")}
            />
            <FormTextArea
              wrapperClassName="col-12"
              label={t("formDescEn")}
              {...register("publishingFormDetails.1.description")}
              error={t(errors.publishingFormDetails?.[1]?.description?.message ?? "", { length: 3 })}
            />
          </div>
          <div className="row">
             <LookupDropdown
              withAsterisk
              wrapperClassName="col-md-4 col-sm-12 mb-2"
              label={t("TypeOfScientific")}
              service="lookupService"
              endpoint="getAllTypeOfScientificDropDown"
              formProps={register("typeOfScientificId", { valueAsNumber: true })}
              textValueKey="details.0.displayName"
              idValueKey="id"
              error={t(errors.typeOfScientificId?.message ?? "")}
              onChange={(value) => {
                setValue("typeOfScientificId", + value);
              }}
              value={watch("typeOfScientificId")}
              changeEffect={() => {
                setValue("typeOfScientificId", 0);
              }}
              useReactSelect
            /> 
            <LookupDropdown
              withAsterisk
              wrapperClassName="col-md-4 col-sm-12 mb-2"
              label={t("ScientificSpecialization")}
              service="lookupService"
              endpoint="getAllScientificClassificationDropDown"
              formProps={register("scientificClassificationId", { valueAsNumber: true })}
              textValueKey="scientificClassificationDetail.displayName"
              idValueKey="id"
              error={t(errors.scientificClassificationId?.message ?? "")}
              onChange={(value) => {
                setValue("scientificClassificationId", + value);
              }}
              value={watch("scientificClassificationId")}
              changeEffect={() => {
                setValue("scientificClassificationId", 0);
              }}
              useReactSelect
            />
          </div>
        </AccordionItem>
      </Accordion>

      <div className="divider mx-4" />
        <Accordion>
        <AccordionItem initiallyOpen title={t("Common:description")} elementId={3}>
          <FormBuilderComponent
            formSchema={getValues().jsonSchema ? JSON.parse(getValues().jsonSchema ?? "{}") : {}}
            setFormSchema={(data) => {
              setValue("jsonSchema", JSON.stringify(data));
            }}
          />
        </AccordionItem>
      </Accordion>
    </form>
  );
};

export default FormTemplateFormAddUpdateView;
